import { useEffect, useMemo, useState } from 'react';

import {
  Button,
  Center,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  Spinner,
  Switch,
  Tag,
  TagLabel,
  Text,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import {
  AddCircleOutline,
  CheckOutline,
  DeleteOutline,
  useToast,
} from '@aslanpay/portal-common-lib';

import ProfileSection from 'app/components/UI/ProfileSection';

import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useUpdateCompanyConfiguration from 'app/hooks/useUpdateCompanyConfiguration';

import { formatCurrency } from 'app/utils/strings';

import CompanyValuesSection from './CompanyValues/CompanyValuesSection';

import {
  RewardAndRecognitionFormState,
  RewardAndRecognitionTabProps,
} from './RewardAndRecognitionTab.types';

const POINTS_PER_POUND = 200;
const MAX_PRESET_AMOUNTS = 10;

const pointsToPounds = (points: number) => points / POINTS_PER_POUND;
const poundsToPoints = (pounds: number) => Math.round(pounds * POINTS_PER_POUND);

const formatPresetLabel = (points: number) =>
  `${formatCurrency(pointsToPounds(points), 'GBP')} · ${points.toLocaleString('en-GB')} pts`;

interface ToggleRowProps {
  label: string;
  description: string;
  isChecked: boolean;
  onChange: (checked: boolean) => void;
  testId: string;
  isDisabled?: boolean;
}

function ToggleRow({
  label,
  description,
  isChecked,
  onChange,
  testId,
  isDisabled = false,
}: ToggleRowProps) {
  return (
    <Flex w="full" align="center" justify="space-between" gap="15px">
      <Flex flexDir="column">
        <Text textStyle="smallMedium" color="brand.black">
          {label}
        </Text>
        <Text textStyle="extraSmallRegular" color="brand.darkGray">
          {description}
        </Text>
      </Flex>
      <Switch
        colorScheme="badge.invited"
        size="lg"
        isChecked={isChecked}
        isDisabled={isDisabled}
        onChange={(event) => onChange(event.target.checked)}
        aria-label={label}
        data-testid={testId}
      />
    </Flex>
  );
}

function RewardAndRecognitionTab({ companyId = '' }: RewardAndRecognitionTabProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: companyConfiguration, isInitialLoading } = useFetchCompanyConfiguration(companyId);

  const { mutateAsync: updateCompanyConfigurationAsync } = useUpdateCompanyConfiguration({
    onSuccess: () => {},
    onError: () => {},
  });

  const [isSaving, setIsSaving] = useState(false);
  const [presetInput, setPresetInput] = useState('');

  const initialForm = useMemo<RewardAndRecognitionFormState>(() => {
    const config = companyConfiguration?.rewardAndRecognitionConfig;
    return {
      mandatoryRecognitionValues: config?.mandatoryRecognitionValues ?? false,
      birthdaysEnabled: config?.birthdaysEnabled ?? false,
      workAnniversariesEnabled: config?.workAnniversariesEnabled ?? false,
      campaignsEnabled: config?.campaignsEnabled ?? false,
      requireApprovalForPoints: config?.requireApprovalForPoints ?? false,
      requireApprovalForAllRecognitions: config?.requireApprovalForAllRecognitions ?? false,
      potBudgetNearDepletionThresholdPoints:
        config?.potBudgetNearDepletionThresholdPoints != null
          ? String(config.potBudgetNearDepletionThresholdPoints)
          : '',
      presetPointAmounts: [...(config?.presetPointAmounts ?? [])].sort((a, b) => a - b),
    };
  }, [companyConfiguration]);

  const [form, setForm] = useState<RewardAndRecognitionFormState>(initialForm);

  useEffect(() => {
    setForm(initialForm);
    setPresetInput('');
  }, [initialForm]);

  // The backend refuses to enable campaigns while Reward & Recognition is unprovisioned, so the toggle
  // says so rather than letting the save fail.
  const moduleStatus = companyConfiguration?.modulesConfig?.modules?.REWARD_AND_RECOGNITION;
  const isModuleProvisioned = moduleStatus === 'ENABLED' || moduleStatus === 'DISABLED';

  const updateField = <Key extends keyof RewardAndRecognitionFormState>(
    key: Key,
    value: RewardAndRecognitionFormState[Key]
  ) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const addPreset = () => {
    const pounds = Number(presetInput);

    if (!presetInput.trim() || Number.isNaN(pounds) || pounds <= 0) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Invalid amount',
        description: 'Enter a positive money amount to add a preset.',
      });
      return;
    }

    const points = poundsToPoints(pounds);

    if (points <= 0) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Invalid amount',
        description: 'Enter a money amount that converts to at least 1 point to add a preset.',
      });
      return;
    }
    if (form.presetPointAmounts.includes(points)) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Duplicate preset',
        description: 'That preset amount has already been added.',
      });
      return;
    }

    if (form.presetPointAmounts.length >= MAX_PRESET_AMOUNTS) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Too many presets',
        description: `You can configure at most ${MAX_PRESET_AMOUNTS} preset amounts.`,
      });
      return;
    }

    updateField(
      'presetPointAmounts',
      [...form.presetPointAmounts, points].sort((a, b) => a - b)
    );
    setPresetInput('');
  };

  const removePreset = (points: number) => {
    updateField(
      'presetPointAmounts',
      form.presetPointAmounts.filter((amount) => amount !== points)
    );
  };

  const arePresetsEqual = (first: number[], second: number[]) =>
    first.length === second.length && first.every((value, index) => value === second[index]);

  const hasChanged = useMemo(() => {
    if (form.mandatoryRecognitionValues !== initialForm.mandatoryRecognitionValues) return true;
    if (form.birthdaysEnabled !== initialForm.birthdaysEnabled) return true;
    if (form.workAnniversariesEnabled !== initialForm.workAnniversariesEnabled) return true;
    if (form.campaignsEnabled !== initialForm.campaignsEnabled) return true;
    if (form.requireApprovalForPoints !== initialForm.requireApprovalForPoints) return true;
    if (form.requireApprovalForAllRecognitions !== initialForm.requireApprovalForAllRecognitions) {
      return true;
    }
    if (
      form.potBudgetNearDepletionThresholdPoints !==
      initialForm.potBudgetNearDepletionThresholdPoints
    ) {
      return true;
    }
    return !arePresetsEqual(form.presetPointAmounts, initialForm.presetPointAmounts);
  }, [form, initialForm]);

  const thresholdValue = form.potBudgetNearDepletionThresholdPoints.trim();
  const parsedThreshold = thresholdValue === '' ? null : Number(thresholdValue);
  const isThresholdValid =
    parsedThreshold === null || (Number.isInteger(parsedThreshold) && parsedThreshold > 0);
  const hasPresets = form.presetPointAmounts.length > 0;

  const isSaveDisabled = !hasChanged || isSaving || !hasPresets || !isThresholdValid;

  const handleSave = async () => {
    if (!companyConfiguration) return;

    setIsSaving(true);
    try {
      await updateCompanyConfigurationAsync({
        id: companyConfiguration.companyId,
        rewardAndRecognitionConfig: {
          mandatoryRecognitionValues: form.mandatoryRecognitionValues,
          birthdaysEnabled: form.birthdaysEnabled,
          workAnniversariesEnabled: form.workAnniversariesEnabled,
          campaignsEnabled: form.campaignsEnabled,
          requireApprovalForPoints: form.requireApprovalForPoints,
          requireApprovalForAllRecognitions: form.requireApprovalForAllRecognitions,
          potBudgetNearDepletionThresholdPoints: parsedThreshold ?? undefined,
          presetPointAmounts: form.presetPointAmounts,
        },
      });

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Configuration saved',
        description: 'The reward & recognition configuration was successfully saved.',
      });

      queryClient.invalidateQueries({
        queryKey: ['fetchCompanyConfiguration', companyConfiguration.companyId],
      });
    } catch (error) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'It was not possible to save the reward & recognition configuration.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isInitialLoading) {
    return (
      <Center width="full" py="40px">
        <Spinner color="brand.purple" size="xl" />
      </Center>
    );
  }

  if (companyConfiguration?.modulesConfig?.modules?.REWARD_AND_RECOGNITION === 'UNAVAILABLE') {
    return (
      <Center py="40px">
        <Text textStyle="smallMedium" color="brand.darkGray">
          Provision Reward & Recognition from the Feature Modules tab before configuring it.
        </Text>
      </Center>
    );
  }

  return (
    <Flex flexDir="column" gap="20px" alignItems="stretch" w="full">
      <ProfileSection title="Recognition settings" isEditMode maxW="100%">
        <ToggleRow
          label="Mandatory recognition values"
          description="Require senders to attach at least one company value to a recognition."
          isChecked={form.mandatoryRecognitionValues}
          onChange={(checked) => updateField('mandatoryRecognitionValues', checked)}
          testId="mandatory-recognition-values-switch"
        />
        <ToggleRow
          label="Birthdays"
          description="Automatically celebrate employee birthdays."
          isChecked={form.birthdaysEnabled}
          onChange={(checked) => updateField('birthdaysEnabled', checked)}
          testId="birthdays-enabled-switch"
        />
        <ToggleRow
          label="Work anniversaries"
          description="Automatically celebrate employee work anniversaries."
          isChecked={form.workAnniversariesEnabled}
          onChange={(checked) => updateField('workAnniversariesEnabled', checked)}
          testId="work-anniversaries-enabled-switch"
        />
        <ToggleRow
          label="Campaigns"
          description={
            isModuleProvisioned
              ? 'Campaigns can be set up while this is off. Turn it on to show them to employees.'
              : 'Provision the Reward & Recognition module before enabling campaigns.'
          }
          isChecked={form.campaignsEnabled}
          isDisabled={!isModuleProvisioned}
          onChange={(checked) => updateField('campaignsEnabled', checked)}
          testId="campaigns-enabled-switch"
        />
        <ToggleRow
          label="Require approval for points"
          description="Recognitions that include points must be approved before they are awarded."
          isChecked={form.requireApprovalForPoints}
          onChange={(checked) => updateField('requireApprovalForPoints', checked)}
          testId="require-approval-for-points-switch"
        />
        <ToggleRow
          label="Require approval for all recognitions"
          description="Every recognition must be approved before it is published."
          isChecked={form.requireApprovalForAllRecognitions}
          onChange={(checked) => updateField('requireApprovalForAllRecognitions', checked)}
          testId="require-approval-for-all-recognitions-switch"
        />
      </ProfileSection>

      {companyId && (
        <ProfileSection title="Company values" isEditMode maxW="100%">
          <CompanyValuesSection companyId={companyId} />
        </ProfileSection>
      )}

      <ProfileSection title="Pot budget" isEditMode maxW="100%">
        <FormControl isInvalid={!isThresholdValid}>
          <FormLabel>Near-depletion threshold (points)</FormLabel>
          <Input
            type="number"
            min={1}
            placeholder="Leave empty to disable the alert"
            value={form.potBudgetNearDepletionThresholdPoints}
            onChange={(event) =>
              updateField('potBudgetNearDepletionThresholdPoints', event.target.value)
            }
            data-testid="pot-budget-threshold-input"
          />
          <FormHelperText color={isThresholdValid ? 'brand.darkGray' : 'text.error'}>
            {isThresholdValid
              ? 'Notify when a pot budget drops to this many remaining points.'
              : 'Enter a whole number of points greater than zero, or leave empty.'}
          </FormHelperText>
        </FormControl>
      </ProfileSection>

      <ProfileSection title="Preset amounts" isEditMode maxW="100%">
        <Text textStyle="extraSmallRegular" color="brand.darkGray">
          Configure the quick-select point values available when sending recognition.{' '}
          {POINTS_PER_POUND} points = {formatCurrency(1, 'GBP')}.
        </Text>

        {hasPresets ? (
          <Flex wrap="wrap" gap="10px" data-testid="preset-amounts-list">
            {form.presetPointAmounts.map((points) => (
              <Tag
                key={points}
                size="md"
                fontSize="xs"
                borderRadius="md"
                variant="subtle"
                data-testid={`preset-amount-${points}`}
              >
                <TagLabel>{formatPresetLabel(points)}</TagLabel>
                <IconButton
                  aria-label={`Remove preset ${formatPresetLabel(points)}`}
                  icon={<DeleteOutline boxSize="14px" />}
                  size="xs"
                  variant="ghost"
                  borderRadius="full"
                  ml="2px"
                  onClick={() => removePreset(points)}
                  data-testid={`remove-preset-${points}`}
                />
              </Tag>
            ))}
          </Flex>
        ) : (
          <Text textStyle="extraSmallRegular" color="text.error" data-testid="preset-amounts-empty">
            Add at least one preset amount before saving.
          </Text>
        )}

        <HStack align="flex-end" spacing="10px">
          <FormControl maxW="200px">
            <FormLabel>Add preset amount</FormLabel>
            <InputGroup size="sm">
              <InputLeftAddon borderLeftRadius="md">£</InputLeftAddon>
              <Input
                type="number"
                min={0.01}
                step="0.01"
                borderRightRadius="md"
                placeholder="5"
                value={presetInput}
                onChange={(event) => setPresetInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addPreset();
                  }
                }}
                data-testid="preset-amount-input"
              />
            </InputGroup>
          </FormControl>
          <Button
            leftIcon={<AddCircleOutline boxSize="16px" />}
            size="sm"
            variant="outline"
            onClick={addPreset}
            isDisabled={form.presetPointAmounts.length >= MAX_PRESET_AMOUNTS}
            data-testid="add-preset-amount-button"
          >
            Add
          </Button>
        </HStack>
      </ProfileSection>

      <Button
        leftIcon={<CheckOutline boxSize="20px" />}
        size="sm"
        alignSelf="flex-start"
        onClick={handleSave}
        isLoading={isSaving}
        isDisabled={isSaveDisabled}
        data-testid="save-reward-and-recognition-config-button"
      >
        Save changes
      </Button>
    </Flex>
  );
}

export { RewardAndRecognitionTab };
